import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PRIORITIES, TASK_STATUSES } from "./projectUtils";
import AttachmentPicker from "./AttachmentPicker";
import AssigneePicker from "./AssigneePicker";
import { assigneesOf } from "./taskUtils";

const empty = {
  name: "", description: "", assigned_to: "", assignees: [], priority: "Medium",
  start_date: "", due_date: "", status: "To Do",
  estimated_hours: 0, actual_hours: 0, attachments: [],
};

export default function TaskForm({ open, onOpenChange, initial, projectId, project, users = [], onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial, assignees: assigneesOf(initial) } : empty); }, [open]);

  // Show project team members (PM + devs) as assignee candidates
  const teamIds = new Set([project?.project_manager_id, ...(project?.development_team || [])].filter(Boolean));
  const assignees = users.filter(u => teamIds.has(u.id) || ["admin", "manager"].includes(u.role) || ["developer", "tester"].includes(u.role));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Task name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, assigned_to: form.assignees[0] || "" };
      if (initial?.id) {
        const { comments, worklogs, attachments, ...rest } = payload;
        await api.put(`/tasks/${initial.id}`, rest);
      } else await api.post(`/projects/${projectId}/tasks`, payload);
      toast.success(initial?.id ? "Updated" : "Task created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{initial?.id ? "Edit Task" : "New Task"}</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">Break work into ownable, trackable units for the dev team.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Task Name" required span={2}><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="tf-name" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label={`Assigned To (${form.assignees.length})`} span={2}>
            <AssigneePicker users={assignees} value={form.assignees} onChange={(a) => setForm({ ...form, assignees: a })} testid="tf-assign" />
          </F>
          <F label="Priority">
            <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="tf-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{PRIORITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Start Date"><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="tf-start" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Due Date"><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} data-testid="tf-due" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Status">
            <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="tf-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{TASK_STATUSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Estimated Hours"><Input type="number" value={form.estimated_hours} onChange={e => setForm({...form, estimated_hours: parseFloat(e.target.value) || 0})} data-testid="tf-est" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Actual Hours"><Input type="number" value={form.actual_hours} onChange={e => setForm({...form, actual_hours: parseFloat(e.target.value) || 0})} data-testid="tf-act" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Description" span={2}><Textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="tf-desc" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          {!initial?.id && <F label="Attachments" span={2}><AttachmentPicker value={form.attachments} onChange={(a) => setForm({...form, attachments: a})} testid="tf-att" /></F>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="tf-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !form.name.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="tf-save">
            {busy ? "Saving..." : (initial?.id ? "Save Changes" : "Add Task")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({label, required, children, span = 1}) {
  return (
    <div className={`space-y-1.5 ${span === 2 ? "col-span-2" : ""}`}>
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">
        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
